import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Select, Toast, Button } from '@douyinfe/semi-ui';
import { IconRefresh, IconBookStroked } from '@douyinfe/semi-icons';
import request from '../../utils/request';

const SubjectSelector = ({ value, onChange, placeholder, style, disabled }) => {
    const [loading, setLoading] = useState(false);
    const [subjectList, setSubjectList] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [internalValue, setInternalValue] = useState(null);
    const fetchedUids = useRef(new Set());

    useEffect(() => {
        if (value) {
            setInternalValue(value);
            // 如果当前列表中没有选中的专栏，且之前没有尝试获取过该专栏，则获取专栏详情
            if (!subjectList.some(subject => subject.uid === value) && !fetchedUids.current.has(value)) {
                fetchSubjectDetails([value]);
            }
        } else {
            setInternalValue(null);
        }
    }, [value]);

    const fetchSubjectDetails = async (uids) => {
        try {
            // 标记这些UID已经尝试获取过
            uids.forEach(uid => fetchedUids.current.add(uid));

            const response = await request.get('/go/sp/lesson/listmini_lesson_subject/', {
                params: {
                    uids: uids.join(',')
                }
            });
            if (response.errno === 0 && response.list) {
                setSubjectList(prevList => {
                    const newList = [...prevList];
                    response.list.forEach(subject => {
                        if (!newList.some(existingSubject => existingSubject.uid === subject.uid)) {
                            newList.push(subject);
                        }
                    });
                    return newList;
                });
            } else {
                // 如果没有找到对应的专栏，也要更新状态以避免重复请求
                console.warn('未找到指定的专栏或请求失败');
            }
        } catch (error) {
            console.error('获取专栏详情失败:', error);
        }
    };

    const fetchSubjectList = async () => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/lesson/listmini_lesson_subject/', {
                params: {
                    page: 0,
                    limit: 300,
                    status: -1,
                    key: searchKey
                }
            });
            if (response.errno === 0) {
                setSubjectList(response.list || []);
                // 重置已获取的UID集合，因为我们刚刚获取了完整的列表
                fetchedUids.current.clear();
            } else {
                Toast.error(response.errstr || '获取专栏列表失败');
            }
        } catch (error) {
            console.error('获取专栏列表失败:', error);
            Toast.error('获取专栏列表失败');
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetchSubjectList = useCallback(() => {
        console.log("searchKey", searchKey)
        const timer = setTimeout(() => {
            fetchSubjectList();
        }, 300);
        return () => clearTimeout(timer);
    }, [searchKey]);

    useEffect(() => {
        const cleanup = debouncedFetchSubjectList();
        return () => cleanup();
    }, [searchKey]);

    useEffect(() => {
        fetchSubjectList();
    }, []);

    const handleChange = (selectedUid) => {
        setInternalValue(selectedUid);
        // 如果选择了null或空值，确保清除内部状态
        if (!selectedUid) {
            fetchedUids.current.clear();
        }
        onChange?.(selectedUid);
    };

    const renderCustomOption = (subject) => {
        const optionStyle = {
            display: 'flex',
            paddingLeft: 24,
            paddingTop: 10,
            paddingBottom: 10,
        };
        return (
            <Select.Option
                key={subject.uid}
                value={subject.uid}
                style={optionStyle}
                showTick={true}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontSize: 14 }}>{subject.name}</div>
                </div>
            </Select.Option>
        );
    };

    const renderSelectedItem = (optionNode) => {
        const selectedSubject = subjectList.find(subject => subject.uid === optionNode.value);
        if (!selectedSubject) return optionNode.value;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{selectedSubject.name}</span>
            </div>
        );
    };

    return (
        <Select
            loading={loading}
            value={internalValue}
            onChange={handleChange}
            style={style}
            placeholder={placeholder || '请选择专栏'}
            filter
            searchPosition='dropdown'
            showClear
            filterOption={(input, option) => {
                const name = option?.label?.toString() || '';
                return name.toLowerCase().includes(input.toLowerCase());
            }}
            prefix={<IconBookStroked style={{ color: 'var(--semi-color-primary)' }} />}
            disabled={disabled}
            renderSelectedItem={renderSelectedItem}
            onDropdownVisibleChange={(visible) => {
                if (visible) {
                    setSearchKey('');
                    fetchSubjectList();
                }
            }}
            onSearch={(value) => {
                setSearchKey(value);
                // 延迟执行搜索，避免频繁请求
                debouncedFetchSubjectList();
            }}
            outerTopSlot={
                <Button
                    icon={<IconRefresh />}
                    type="tertiary"
                    onClick={fetchSubjectList}
                    style={{ width: '100%', marginBottom: '8px' }}
                >
                    刷新列表
                </Button>
            }
        >
            {subjectList.map(subject => renderCustomOption(subject))}
        </Select>
    );
};

export default SubjectSelector;